var React = require('react')
var raf = require('./raf')
var printDebug = require('./debug')

var dev = process.env.NODE_ENV !== 'production'
var isServer = typeof navigator === 'undefined'

function createContext () {
  var AtomContext = React.createContext()

  var Provider = /*@__PURE__*/(function (superclass) {
    function Provider () {
      superclass.apply(this, arguments);
    }

    if ( superclass ) Provider.__proto__ = superclass;
    Provider.prototype = Object.create( superclass && superclass.prototype );
    Provider.prototype.constructor = Provider;

    Provider.prototype.render = function render () {
      return (
        React.createElement( AtomContext.Provider, { value: { atom: this.props.atom, debug: this.props.debug } },
          this.props.children
        )
      )
    };

    return Provider;
  }(React.Component));

  var ConsumerInner = /*@__PURE__*/(function (superclass) {
    function ConsumerInner (props) {
      var this$1 = this;

      superclass.call(this)
      this.state = {}
      this.isPure = typeof props.pure === 'undefined' ? true : props.pure
      this.shouldObserve = typeof props.observe === 'undefined' ? !isServer : props.observe
      this.scheduleUpdate = props.sync ? function () { return this$1.update(); } : raf(function () { return this$1.update(); })
    }

    if ( superclass ) ConsumerInner.__proto__ = superclass;
    ConsumerInner.prototype = Object.create( superclass && superclass.prototype );
    ConsumerInner.prototype.constructor = ConsumerInner;

    ConsumerInner.prototype.observe = function observe () {
      var this$1 = this;

      if (!this.shouldObserve) { return }
      this.unobserve && this.unobserve()
      this.unobserve = this.props.atom.observe(function () { this$1.cancelUpdate = this$1.scheduleUpdate() })
      this.observedAtom = this.props.atom
      delete this.boundActions
      delete this.boundActionsSpec
    };

    ConsumerInner.prototype.componentWillUnmount = function componentWillUnmount () {
      this.unobserve && this.unobserve()
      this.cancelUpdate && this.cancelUpdate()
      delete this.unobserve
      delete this.cancelUpdate
      delete this.observedAtom
      delete this.boundActions
      delete this.boundActionsSpec
    };

    ConsumerInner.prototype.shouldComponentUpdate = function shouldComponentUpdate (nextProps, nextState) {
      if (!this.isPure) { return true }

      // if it's <Consumer> with dynamic children, shortcut the check
      if (this.props.children !== nextProps.children) {
        if (dev && (this.context.debug || this.props.debug)) {
          printDebug.children(this.debugName(), this.props.children, nextProps.children)
        }
        return true
      }

      // our state is mappedProps, this is the main optimisation
      if (differ(this.state, nextState)) {
        if (dev && (this.context.debug || this.props.debug)) {
          printDebug.props(this.debugName(), this.state, nextState)
        }
        return true
      }

      // in connect() case don't need to diff further, no extra props
      if (this.props.originalProps) {
        this.cancelUpdate && this.cancelUpdate()
        return false
      }

      // in <Consumer /> case we also diff props
      if (differ(this.props, nextProps)) {
        if (dev && (this.context.debug || this.props.debug)) {
          printDebug.props(this.debugName(), this.props, nextProps)
        }
        return true
      }

      this.cancelUpdate && this.cancelUpdate()
      return false
    };

    ConsumerInner.prototype.debugName = function debugName () {
      return this.props.displayName || this.constructor.name
    };

    ConsumerInner.prototype.componentDidUpdate = function componentDidUpdate (prevProps) {
      this.cancelUpdate && this.cancelUpdate()
    };

    ConsumerInner.prototype.update = function update () {
      this.setState({})
    };

    ConsumerInner.prototype.bindActions = function bindActions (actions, dispatch, mappedProps) {
      if (!actions) { return { dispatch: dispatch } }
      if (typeof actions === 'function') { return actions(dispatch, mappedProps) }
      if (!this.boundActions || this.boundActionsSpec !== actions) {
        this.boundActionsSpec = actions
        this.boundActions = actions.reduce(function (acc, action) {
          acc[action] = function (payload) { return dispatch(action, payload); }
          return acc
        }, {})
      }
      return this.boundActions
    };

    ConsumerInner.prototype.render = function render () {
      // do this in render, because:
      //  doing in constructor would cause memory leaks in SSR
      //  doing in componentDidMount leads to the wrong order of subscriptions
      if (!this.unobserve || this.observedAtom !== this.props.atom) { this.observe() }
      var ref = this.props;
      var atom = ref.atom;
      var actions = ref.actions;
      var originalProps = ref.originalProps;
      var render = ref.render;
      var children = ref.children;
      var mappedProps = this.state
      var boundActions = this.bindActions(actions, atom.dispatch, mappedProps)
      return (render || children)(Object.assign({}, originalProps, mappedProps, boundActions))
    };

    return ConsumerInner;
  }(React.Component));

  ConsumerInner.getDerivedStateFromProps = function (props, state) {
    var atom = props.atom;
    var originalProps = props.originalProps;
    var map = props.map;
    return Object.assign({}, originalProps, map ? map(atom.get(), originalProps) : {})
  }

  var Consumer = function (props) { return (
    React.createElement( AtomContext.Consumer, null,
      function (ref) {
        var atom = ref.atom;
        var debug = ref.debug;

        return React.createElement( ConsumerInner, Object.assign({}, props, { atom: atom, debug: debug }));
    }
    )
  ); }

  function connect (map, actions, options) {
    if ( options === void 0 ) options = {};

    return function connectComponent (Component) {
      var render = function (mappedProps) { return React.createElement( Component, mappedProps); }
      var Connected = function (props) { return (
        React.createElement( Consumer, {
          displayName: Component.displayName || Component.name, map: map, actions: actions, pure: options.pure, sync: options.sync, observe: options.observe, debug: options.debug, originalProps: props, render: render })
      ); }
      return Connected
    }
  }

  function differ (mappedProps, nextMappedProps) {
    if (mappedProps === nextMappedProps) {
      return false
    }
    if (!mappedProps || !nextMappedProps) {
      return true
    }
    if (!isObject(mappedProps) || !isObject(nextMappedProps)) {
      return true
    }
    for (var i in mappedProps) {
      if (mappedProps[i] !== nextMappedProps[i]) { return true }
    }
    for (var i$1 in nextMappedProps) {
      if (!(i$1 in mappedProps)) { return true }
    }
    return false
  }

  function isObject (obj) {
    return typeof obj === 'object' &&
      Object.prototype.toString.call(obj) === '[object Object]'
  }

  return { AtomContext: AtomContext, Provider: Provider, Consumer: Consumer, connect: connect, differ: differ }
}

var ref = createContext();
var AtomContext = ref.AtomContext;
var Provider = ref.Provider;
var Consumer = ref.Consumer;
var connect = ref.connect;
var differ = ref.differ;
module.exports = { AtomContext: AtomContext, Provider: Provider, Consumer: Consumer, connect: connect, differ: differ, createContext: createContext }
